import React from 'react';
import { Box, Container, Heading, Text, VStack, HStack, Badge, SimpleGrid, Icon } from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { FaCheck, FaClock } from 'react-icons/fa';

const MotionBox = motion(Box);

const AIMasteryOutcomes = ({ data }) => {
  return (
    <Box py={{ base: 16, md: 20 }} bg="white">
      <Container maxW="container.xl">
        <VStack spacing={12}>
          {/* Title */}
          <VStack spacing={4} textAlign="center">
            <Heading
              size="2xl"
              fontWeight="900"
              color="brand.800"
            >
              {data.title}
            </Heading>
            {data.subtitle && (
              <Text fontSize="lg" color="brand.600" maxW="3xl">
                {data.subtitle}
              </Text>
            )}
          </VStack>

          {/* Technical Milestones */}
          {data.technicalMilestones && (
            <VStack spacing={6} w="full" align="stretch">
              <Heading size="lg" color="brand.700" textAlign="center">
                Technical Milestones
              </Heading>
              {data.technicalMilestones.map((milestone, index) => (
                <MotionBox
                  key={index}
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.15 }}
                >
                  <Box
                    p={6}
                    bg="purple.50"
                    borderRadius="xl"
                    borderLeft="6px solid"
                    borderColor="purple.500"
                    _hover={{
                      transform: 'translateX(8px)',
                      boxShadow: '0 8px 30px rgba(128,90,213,0.15)',
                    }}
                    transition="all 0.3s"
                  >
                    <VStack align="flex-start" spacing={2}>
                      <HStack spacing={4}>
                        <Badge
                          px={3}
                          py={1}
                          bg="purple.600"
                          color="white"
                          fontSize="xs"
                          fontWeight="700"
                          borderRadius="md"
                          flexShrink={0}
                        >
                          {milestone.timeline}
                        </Badge>
                        <Text
                          fontSize="lg"
                          fontWeight="700"
                          color="brand.800"
                        >
                          {milestone.achievement}
                        </Text>
                      </HStack>
                      {milestone.impact && (
                        <Text fontSize="sm" color="purple.600" fontWeight="600" pl={2}>
                          {milestone.impact}
                        </Text>
                      )}
                    </VStack>
                  </Box>
                </MotionBox>
              ))}
            </VStack>
          )}

          {/* Career Milestones */}
          {data.careerMilestones && (
            <VStack spacing={6} w="full" align="stretch">
              <Heading size="lg" color="brand.700" textAlign="center">
                Career Milestones
              </Heading>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                {data.careerMilestones.map((milestone, index) => (
                  <MotionBox
                    key={index}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                  >
                    <Box
                      p={6}
                      bg="green.50"
                      borderRadius="xl"
                      h="full"
                      _hover={{
                        transform: 'translateY(-4px)',
                        boxShadow: '0 8px 30px rgba(16,185,129,0.15)',
                      }}
                      transition="all 0.3s"
                    >
                      <HStack spacing={3} align="flex-start">
                        <Icon as={FaClock} color="accent.green" boxSize={5} mt={1} />
                        <VStack align="flex-start" spacing={1}>
                          <Text fontSize="sm" fontWeight="700" color="accent.green">
                            {milestone.timeline}
                          </Text>
                          <Text fontSize="md" fontWeight="600" color="brand.700">
                            {milestone.achievement}
                          </Text>
                        </VStack>
                      </HStack>
                    </Box>
                  </MotionBox>
                ))}
              </SimpleGrid>
            </VStack>
          )}

          {/* ROI Metrics */}
          {data.roiMetrics && (
            <MotionBox
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              w="full"
            >
              <Box
                p={8}
                bgGradient="linear(to-r, purple.600, purple.800)"
                borderRadius="2xl"
              >
                <VStack spacing={6} color="white">
                  <Heading size="xl" textAlign="center">
                    {data.roiMetrics.title}
                  </Heading>
                  
                  <Text fontSize="3xl" fontWeight="900" textAlign="center">
                    {data.roiMetrics.timeSaved}
                  </Text>

                  {data.roiMetrics.breakdown && (
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} w="full">
                      {data.roiMetrics.breakdown.map((item, index) => (
                        <Box key={index} p={4} bg="whiteAlpha.200" borderRadius="lg">
                          <HStack justify="space-between">
                            <Text fontWeight="600">{item.task}</Text>
                            <Badge colorScheme="green" fontSize="sm">{item.saved} saved</Badge>
                          </HStack>
                          <HStack fontSize="sm" mt={2} opacity={0.9}>
                            <Text>{item.before}</Text>
                            <Text>→</Text>
                            <Text>{item.after}</Text>
                          </HStack>
                        </Box>
                      ))}
                    </SimpleGrid>
                  )}

                  <VStack spacing={2} textAlign="center" pt={4}>
                    <Text fontSize="lg" fontWeight="700">{data.roiMetrics.monthlyValue}</Text>
                    <Text fontSize="md">{data.roiMetrics.paybackPeriod}</Text>
                    <Text fontSize="xl" fontWeight="700" color="accent.gold">{data.roiMetrics.careerImpact}</Text>
                  </VStack>
                </VStack>
              </Box>
            </MotionBox>
          )}

          {/* Fallback to old structure */}
          {data.milestones && !data.technicalMilestones && (
            <VStack spacing={6} w="full" align="stretch">
              {data.milestones.map((milestone, index) => (
                <MotionBox
                  key={index}
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.15 }}
                >
                  <Box
                    p={6}
                    bg="purple.50"
                    borderRadius="xl"
                    borderLeft="6px solid"
                    borderColor="purple.500"
                    _hover={{
                      transform: 'translateX(8px)',
                      boxShadow: '0 8px 30px rgba(128,90,213,0.15)',
                    }}
                    transition="all 0.3s"
                  >
                    <HStack spacing={4} align="flex-start">
                      <Badge
                        px={3}
                        py={1}
                        bg="purple.600"
                        color="white"
                        fontSize="xs"
                        fontWeight="700"
                        borderRadius="md"
                        flexShrink={0}
                      >
                        {milestone.timeline}
                      </Badge>
                      <VStack align="flex-start" spacing={1}>
                        <Text fontSize="lg" fontWeight="700" color="brand.800">
                          {milestone.achievement}
                        </Text>
                      </VStack>
                    </HStack>
                  </Box>
                </MotionBox>
              ))}
            </VStack>
          )}

          {/* Old Metric */}
          {data.metric && !data.roiMetrics && (
            <MotionBox
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.8 }}
              w="full"
            >
              <Box
                p={8}
                bgGradient="linear(to-r, purple.600, purple.800)"
                borderRadius="2xl"
                textAlign="center"
              >
                <HStack justify="center" spacing={3}>
                  <Box p={2} bg="whiteAlpha.300" borderRadius="full">
                    <FaCheck color="white" size={20} />
                  </Box>
                  <Text fontSize={{ base: 'lg', md: 'xl' }} fontWeight="700" color="white">
                    {data.metric}
                  </Text>
                </HStack>
              </Box>
            </MotionBox>
          )}

          {/* Visual Placeholder */}
          {data.visualPlaceholder && (
            <Box
              p={6}
              bg="gray.100"
              borderRadius="xl"
              borderStyle="dashed"
              borderWidth="2px"
              borderColor="gray.300"
              textAlign="center"
              w="full"
            >
              <Text color="gray.600" fontSize="sm" fontStyle="italic">
                {data.visualPlaceholder}
              </Text>
            </Box>
          )}
        </VStack>
      </Container>
    </Box>
  );
};

export default AIMasteryOutcomes;
